import type { NostrEvent } from '@nostrify/nostrify';
import { getTagValue, parseAddressPointer, type AddressPointer } from '@/lib/nip54';

/** NKBIP-01 publication index (addressable). Table-of-contents event referencing ordered sections. */
export const PUBLICATION_INDEX_KIND = 30040;
/** NKBIP-01 publication content (addressable). A section/chapter/episode of a publication. */
export const PUBLICATION_CONTENT_KIND = 30041;

/** Kinds an index's `a` tags are expected to reference (sections, nested indices, or long-form/wiki content). */
export const PUBLICATION_LEAF_KINDS = [PUBLICATION_CONTENT_KIND, PUBLICATION_INDEX_KIND, 30817, 30818, 30023] as const;

export type PublicationType = 'book' | 'illustrated' | 'magazine' | 'documentation' | 'academic' | 'blog';

export interface PublicationSectionRef {
  pointer: AddressPointer;
  relayHint?: string;
  eventId?: string;
}

/** A publication index MUST have a non-empty `content` and a `title` tag; `d` uniquely identifies it together with pubkey+kind. */
export function isValidPublicationIndex(event: NostrEvent): boolean {
  if (event.kind !== PUBLICATION_INDEX_KIND) return false;
  if (event.content !== '') return false;
  const d = getTagValue(event, 'd');
  const title = getTagValue(event, 'title');
  return Boolean(d && title);
}

/** A publication section MUST have a `d` tag and a `title` tag. */
export function isValidPublicationSection(event: NostrEvent): boolean {
  if (event.kind !== PUBLICATION_CONTENT_KIND) return false;
  const d = getTagValue(event, 'd');
  const title = getTagValue(event, 'title');
  return Boolean(d && title);
}

/** Extracts the ordered list of section/sub-index references from a publication index's `a` tags. */
export function getPublicationRefs(event: NostrEvent): PublicationSectionRef[] {
  return event.tags
    .filter(([name]) => name === 'a')
    .map(([, value, relayHint, eventId]) => {
      const pointer = parseAddressPointer(value);
      if (!pointer) return undefined;
      return { pointer, relayHint: relayHint || undefined, eventId: eventId || undefined };
    })
    .filter((ref): ref is PublicationSectionRef => Boolean(ref));
}

export function publicationTitle(event: NostrEvent): string {
  return getTagValue(event, 'title') || getTagValue(event, 'd') || 'Untitled publication';
}

export function publicationSummary(event: NostrEvent): string | undefined {
  return getTagValue(event, 'summary');
}

export function publicationImage(event: NostrEvent): string | undefined {
  return getTagValue(event, 'image');
}

export function publicationType(event: NostrEvent): PublicationType {
  const type = getTagValue(event, 'type');
  const known: PublicationType[] = ['book', 'illustrated', 'magazine', 'documentation', 'academic', 'blog'];
  return known.includes(type as PublicationType) ? (type as PublicationType) : 'book';
}

export interface PublicationAuthor {
  name: string;
  role?: string;
}

/** Extracts all `author` tags: `["author", "Full Name", "role"?]`. */
export function getPublicationAuthors(event: NostrEvent): PublicationAuthor[] {
  return event.tags
    .filter(([name]) => name === 'author')
    .map(([, name, role]) => ({ name, role: role || undefined }))
    .filter((a) => Boolean(a.name));
}

export interface PublicationIdentifier {
  /** The scheme before the colon, e.g. `isbn`, `openlibrary`, `wikidata`, `gutenberg`, `wikipedia`, `overdrive`. */
  type: string;
  /** Everything after the first colon. */
  value: string;
}

/** Well-known `i` tag identifier schemes, per the NKBIP-01 example event. Not exhaustive — clients may use others. */
export const KNOWN_IDENTIFIER_TYPES = ['isbn', 'openlibrary', 'wikidata', 'wikipedia', 'gutenberg', 'overdrive'] as const;

/** Extracts all `i` tags (external identifiers): `["i", "scheme:value"]`. */
export function getPublicationIdentifiers(event: NostrEvent): PublicationIdentifier[] {
  return event.tags
    .filter(([name]) => name === 'i')
    .map(([, raw]) => {
      const idx = raw?.indexOf(':') ?? -1;
      if (!raw || idx === -1) return undefined;
      return { type: raw.slice(0, idx), value: raw.slice(idx + 1) };
    })
    .filter((id): id is PublicationIdentifier => Boolean(id?.type && id.value));
}

/** Best-effort resolution of an `i` tag identifier into a browsable URL, for known schemes only. */
export function identifierUrl(id: PublicationIdentifier): string | undefined {
  switch (id.type) {
    case 'isbn':
      return `https://openlibrary.org/isbn/${encodeURIComponent(id.value)}`;
    case 'openlibrary':
      return `https://openlibrary.org/works/${encodeURIComponent(id.value)}`;
    case 'wikidata':
      return `https://www.wikidata.org/wiki/${encodeURIComponent(id.value)}`;
    case 'gutenberg':
      return `https://www.gutenberg.org/ebooks/${encodeURIComponent(id.value)}`;
    case 'overdrive':
      return undefined; // no stable public URL scheme
    case 'wikipedia': {
      // Format is `wikipedia:<lang>:<Article_Title>`, but the type/value
      // split above only separates on the first colon, so `value` here
      // still contains `<lang>:<Article_Title>`.
      const [lang, ...rest] = id.value.split(':');
      const title = rest.join(':');
      if (!lang || !title) return undefined;
      return `https://${encodeURIComponent(lang)}.wikipedia.org/wiki/${encodeURIComponent(title)}`;
    }
    default:
      return undefined;
  }
}

export function publicationVersion(event: NostrEvent): string | undefined {
  return getTagValue(event, 'version');
}

export function publicationPublishedOn(event: NostrEvent): string | undefined {
  return getTagValue(event, 'published_on');
}

export function publicationPublishedBy(event: NostrEvent): string | undefined {
  return getTagValue(event, 'published_by');
}

/** The `s` (source) tag — a URL to the original text this publication was derived from. */
export function publicationSource(event: NostrEvent): string | undefined {
  return getTagValue(event, 's');
}

/** Topical `t` tags. */
export function getPublicationTopics(event: NostrEvent): string[] {
  return event.tags.filter(([name, value]) => name === 't' && value).map(([, value]) => value);
}

export interface DerivativeWorkInfo {
  originalAuthorPubkey: string;
  originalEventId: string;
  relayHint?: string;
}

/**
 * For derivative works, NKBIP-01 requires a `p` tag identifying the
 * original author immediately followed by an `E` tag referencing the
 * original event. Returns `undefined` if this index isn't marked as a
 * derivative work.
 */
export function getDerivativeWorkInfo(event: NostrEvent): DerivativeWorkInfo | undefined {
  const pIndex = event.tags.findIndex(([name]) => name === 'p');
  if (pIndex === -1) return undefined;
  const eTag = event.tags[pIndex + 1];
  if (!eTag || eTag[0] !== 'E' || !eTag[1]) return undefined;
  return {
    originalAuthorPubkey: event.tags[pIndex][1],
    originalEventId: eTag[1],
    relayHint: eTag[2] || undefined,
  };
}

export function sectionTitle(event: NostrEvent): string {
  return getTagValue(event, 'title') || getTagValue(event, 'd') || 'Untitled section';
}
